/**
 * getItemSubtitle — derives a short metadata subtitle for an item based on
 * its category slug and enrichment properties.
 *
 * Mirrors the logic in the quick-add sheet search results, so ItemCards and
 * search result rows show consistent metadata.
 */

type Property = { key: string; value: string };

function prop(properties: Property[], key: string): string | undefined {
  return properties.find((p) => p.key === key)?.value;
}

export function getItemSubtitle(
  categorySlug: string | null | undefined,
  properties: Property[]
): string | null {
  if (!categorySlug || !properties?.length) return null;

  if (categorySlug === 'movie') {
    const year = prop(properties, 'year');
    const director = prop(properties, 'director');
    return [year && `(${year})`, director].filter(Boolean).join(' · ') || null;
  }

  if (categorySlug === 'tv-show') {
    const year = prop(properties, 'year');
    const seasons = prop(properties, 'seasons');
    return [year && `(${year})`, seasons].filter(Boolean).join(' · ') || null;
  }

  if (categorySlug === 'book') {
    const author = prop(properties, 'author');
    const year = prop(properties, 'year');
    return [author, year && `(${year})`].filter(Boolean).join(' · ') || null;
  }

  if (categorySlug === 'restaurant' || categorySlug === 'place') {
    const address = prop(properties, 'address');
    const rating = prop(properties, 'rating');
    const shortAddress = address
      ? address.split(',').slice(-2).join(',').trim()
      : undefined;
    return [rating && `★ ${rating.split(' ')[0]}`, shortAddress].filter(Boolean).join(' · ') || null;
  }

  if (categorySlug === 'podcast') {
    const author = prop(properties, 'author') ?? prop(properties, 'artist');
    return author ?? null;
  }

  return null;
}
